import {browserCrypto, Random} from "random-js";

import {
  IPerRoadScenarioImperial,
  IPerRoadSeasonImperial,
  PerRoadLayerType,
  PrAxleConfigurationType,
} from "@/modules/perRoad/interfaces/perRoad";
import {sampleDistribution} from "@/lib/monteCarlo/pdf";
import {
  IPrMonteCarloDrawImperial,
  IPrSimulatedLayerImperial,
  IPrTireLoadImperial,
  IWesleaConfig,
  IWesleaConfigBatch,
} from "@/modules/perRoad/interfaces/monteCarlo";
import {axlePatterns} from "@/modules/perRoad/lib/simulation/axleLoadPatterns";
import CryptoJS from "crypto-js";
import {UnitSystem} from "@/interfaces/units";
import {v4} from "uuid";

/**
 * RNG for this function
 */

const random = new Random(browserCrypto);


/**
 * Simple hashing function to track draws round-trip
 * @param uniqueStr
 */

const getHash = (uniqueStr: string): string => {
  // let hash = 0;
  // let i;
  // let chr;
  //
  // for (i = 0; i < uniqueStr.length; i += 1) {
  //   chr = uniqueStr.charCodeAt(i);
  //   // eslint-disable-next-line no-bitwise
  //   hash = ((hash << 5) - hash) + chr;
  //   // eslint-disable-next-line no-bitwise
  //   hash |= 0;

  // }

  const hash = CryptoJS.MD5(uniqueStr).toString();//String(hash);

  return hash;
};


/**
 * Create a cumulative distribution for use with RNG to generate draws
 * @param buckets: numerical array representing bucket widths
 */
const createCumulativeDistArray = (buckets: number[]): number[] => {
  const bucketCumul: number[] = [];
  buckets.forEach((x, i) => {
    if (i > 0) {
      bucketCumul[i] = bucketCumul[i - 1] + x;
    } else {
      bucketCumul[i] = x;
    }
  });
  return bucketCumul;
};


/**
 * Generate an axle type given a percent distribution of axle types
 *
 * Pcts must add to 100!
 *
 * @param singlePct -> 0-100
 * @param tandemPct -> 0-100
 * @param tridemPct -> 0-100
 * @param steerPct -> 0-100
 */

const drawAxleType = (singlePct: number = 0,
                      tandemPct: number = 0,
                      tridemPct: number = 0,
                      steerPct: number = 0): PrAxleConfigurationType => {
  const singleThreshold = singlePct;
  const tandemThreshold = singlePct + tandemPct;
  const tridemThreshold = tandemThreshold + tridemPct;
  const steerThreshold = tridemThreshold + steerPct;

  const rn = random.real(0, 100);

  let axleType: PrAxleConfigurationType;

  if (rn <= singleThreshold) {
    axleType = PrAxleConfigurationType.SINGLE;
  } else if (rn <= tandemThreshold) {
    axleType = PrAxleConfigurationType.TANDEM;
  } else if (rn <= tridemThreshold) {
    axleType = PrAxleConfigurationType.TRIDEM;
  } else {
    axleType = PrAxleConfigurationType.STEER;
  }

  // console.log(`Drawing Axle Type ${singlePct} | ${tandemPct} | ${tridemPct} -> ${axleType}`);

  return axleType;
};


/**
 * Create a spatial loading pattern matching the axle configuration
 * @param axleType
 * @param axleLoad_kip: axle load in thousands of lb (80 kip if vehicle total weight is 80,000lb)
 */
const createAxleSpatialLoading = (axleType: PrAxleConfigurationType, axleLoad_kip: number) => {
  const axleTypePatterns = axlePatterns[axleType];
  const numPatterns = Object.keys(axleTypePatterns).length;
  const rn = random.integer(0, numPatterns - 1);
  const patternName = Object.keys(axleTypePatterns)[rn];
  const loadingPattern = axleTypePatterns[patternName];

  // Load is spread across double the locations listed (both sides of axle)
  const loadPerTire = axleLoad_kip / (loadingPattern.length * 2);

  return loadingPattern.map((tire): IPrTireLoadImperial => {
      return {
        ...tire,
        load_lbf: loadPerTire * 1000,
      };
    }
  );
};

/**
 * Randomly generate and axle load based on a discrete load distribution probability density function.
 *
 * @param axleLoadDist: array of percents (0-100) in order by loading (2kip per entry).
 */
const drawAxleLoad = (axleLoadDist: number[]): number => {
  const bucketsCumulative = createCumulativeDistArray(axleLoadDist);
  const rn = random.real(0, 100);
  const bucket = bucketsCumulative.findIndex((x) => x >= rn);
  const axleLoad_kip = bucket * 2 + 1; // Buckets are 2kip wide, assume middle of bucket
  return axleLoad_kip;
};


/**
 * Randomly generate a season based on relative number of weeks.
 *
 * @param seasons
 */
const drawSeason = (seasons: IPerRoadSeasonImperial[]) => {
  const totalWeeks = seasons.map((s) => {
    if (s.enabled) {
      return s.duration_weeks;
    }
    return 0;
  }).reduce((a, b) => a + b, 0);
  const rn = random.real(0, totalWeeks);
  const cumDist = createCumulativeDistArray(seasons.map((s) => s.duration_weeks));

  const seasonIndex = cumDist.findIndex((x) => x >= rn);
  if (!seasons[seasonIndex].enabled) {
    throw("Season not enabled!");
  }
  return seasonIndex;
};

/**
 * Create a complete monte carlo draw
 *
 * @param scenario
 */

export const getMonteCarloDraw = (scenario: IPerRoadScenarioImperial) => {
  /**
   * Generate unique ID for this draw
   */

  const now = performance.now();
  const randInt = random.int32();

  const hash = getHash(`${scenario.id} ${now} ${randInt}`);


  /**
   * Get season
   */

  const seasonIndex = drawSeason(scenario.seasons);
  // console.log('Season', seasonIndex, scenario.layers[0].modulus_psi);

  /**
   * Create pavement layers
   */
  const layers: IPrSimulatedLayerImperial[] = [];

  let depthOfLayerTop_in = 0;
  let depthOfLayerBottom_in = 0;

  scenario.layers.forEach((layer, i) => {
    if (layer.enabled) {
      // console.log('LayerSeasons', layer.modulus_psi.length);
      const thickness_in = sampleDistribution(layer.thickness_in);

      depthOfLayerBottom_in += thickness_in;

      // Modulus values
      let modulus_psi: number;
      if (layer.type === (PerRoadLayerType.AC || layer.type === PerRoadLayerType.CRACKED_AC) && layer.modulus_psi.length >= seasonIndex) {
        modulus_psi = sampleDistribution(layer.modulus_psi[seasonIndex]);
        // console.log("Sampled modulus", modulus_psi, "from", layer.modulus_psi[seasonIndex]);
      } else {
        // Not a season-dependent AC layer
        modulus_psi = sampleDistribution(layer.modulus_psi[0]);
      }

      // Poisson Ratios
      // const poissonPdf = getPdfFromDistribution(layer.poissonRatio);

      layers.push({
        id: layer.id || -1,
        name: layer.name || `Layer ${i}`,
        type: layer.type,
        unitSystem: UnitSystem.Imperial,
        layerNumber: i + 1,
        modulus_psi,
        poissonRatio: layer.poissonRatio,
        thickness_in,
        depthOfLayerTop_in,
        depthOfLayerBottom_in,
      });

      depthOfLayerTop_in += thickness_in;
    }
  });


  /**
   * Create loading scenario
   */

    // Axle type
  let singlePct = scenario.vehiclesPctAadt.map(
    (pct, i) => (pct / 100) * scenario.vehicleAxlesSingle[i],
    ).reduce((a, b) => a + b, 0);

  let tandemPct = scenario.vehiclesPctAadt.map(
    (pct, i) => (pct / 100) * scenario.vehicleAxlesTandem[i],
  ).reduce((a, b) => a + b, 0);

  let tridemPct = scenario.vehiclesPctAadt.map(
    (pct, i) => (pct / 100) * scenario.vehicleAxlesTridem[i],
  ).reduce((a, b) => a + b, 0);

  let steerPct = scenario.vehiclesPctAadt.map(
    (pct, i) => (pct / 100) * scenario.vehicleAxlesSteer[i],
  ).reduce((a, b) => a + b, 0);

  // Sum if enabled, otherwise ignore axle type
  singlePct *= (scenario.axleSingleEnabled ? 1 : 0);
  tandemPct *= (scenario.axleTandemEnabled ? 1 : 0);
  tridemPct *= (scenario.axleTridemEnabled ? 1 : 0);
  steerPct *= (scenario.axleSteerEnabled ? 1 : 0);

  const total = singlePct + tandemPct + tridemPct + steerPct;

  // Get normalized axle type distribution
  singlePct = (singlePct / total) * 100;
  tandemPct = (tandemPct / total) * 100;
  tridemPct = (tridemPct / total) * 100;
  steerPct = (steerPct / total) * 100;

  const axleType = drawAxleType(singlePct, tandemPct, tridemPct, steerPct);

  // Axle weight
  let axleLoad_kip = 0;
  switch (axleType) {
    case PrAxleConfigurationType.SINGLE:
      axleLoad_kip = drawAxleLoad(scenario.axleLoadDistSingle);
      break;
    case PrAxleConfigurationType.TANDEM:
      axleLoad_kip = drawAxleLoad(scenario.axleLoadDistTandem);
      break;
    case PrAxleConfigurationType.TRIDEM:
      axleLoad_kip = drawAxleLoad(scenario.axleLoadDistTridem);
      break;
    default:
      axleLoad_kip = drawAxleLoad(scenario.axleLoadDistSingle);
      break;
  }

  // Set specific axle type, configuration, spacing, number of tires, etc.
  const loads = createAxleSpatialLoading(axleType, axleLoad_kip);

  const result: IPrMonteCarloDrawImperial = {
    id: hash,
    scenarioId: scenario.id || 0,
    unitSystem: UnitSystem.Imperial,
    loads,
    layers,
    axleType,
    axleLoad_kip,
    season: seasonIndex,
    wesleaConfig: null,
    wesleaResult: null,
    horizontalStrain: null,
  };

  // console.log(`---> ${result.id}`);

  return result;
};


/**
 * Convert the monte carlo draw into the format expected by Weslea
 * @param draw
 */

export const createWesleaConfigFromDraw = (draw: IPrMonteCarloDrawImperial): IWesleaConfig => {
  const subgradeLayer = draw.layers[draw.layers.length - 1];

  const criticallocations: { i: string, l: number, x: number, y: number, z: number }[] = [];

  draw.layers.forEach((layer, i) => {
    draw.loads.forEach((load, j) => {
      if (load.criticalLayout_in.length > 0) {
        load.criticalLayout_in.forEach(loc => {
          const topId = v4();
          const bottomId = v4();

          // Don't generate unnecessary crit points
          if (layer.depthOfLayerTop_in < 500) {
            criticallocations.push({
              i: topId,
              l: layer.layerNumber,
              x: loc.x,
              y: loc.y,
              z: layer.depthOfLayerTop_in,
            });
          }
          if (layer.depthOfLayerBottom_in < 500) {
            criticallocations.push({
              i: bottomId,
              l: layer.layerNumber,
              x: loc.x,
              y: loc.y,
              z: layer.depthOfLayerBottom_in,
            });
          }

        });
      }
    });
  });

  // console.log('NumCritLocs', criticallocations.length);

  const weslea: IWesleaConfig = {
    drawId: draw.id,
    layers: draw.layers.map((L) => (
      {modulus: L.modulus_psi, poissons: L.poissonRatio, thickness: L.thickness_in}
    )),
    loads: draw.loads.map((load) => (
      {
        pressure: load.tirePressure_psi,
        radius: Number(Math.sqrt(load.load_lbf / (Math.PI * load.tirePressure_psi)).toFixed(2)),
        x: load.location_in.x,
        y: load.location_in.y,
      }
    )),
    criticallocations,
    subgrademodulus: subgradeLayer.modulus_psi,
    subgradepoissons: subgradeLayer.poissonRatio,
    layerinterface: [1, 1, 1],
  };

  return weslea;
};


/**
 * Get a batch of monte carlo draws to evaluate
 *
 * @param n: number of draws to perform
 * @param scenario: the user-input scenario configuration
 */

export const getManyDraws = (n: number, scenario: IPerRoadScenarioImperial) => {
  const draws: IPrMonteCarloDrawImperial[] = [];
  const wesleaConfigs: IWesleaConfigBatch = {};

  for (let i = 0; i < n; i += 1) {
    // console.log(`Draw ${i}`);
    const draw = getMonteCarloDraw(scenario);
    draws.push(draw);

    // console.log(`Config ${i}: ${draw.id}`);
    const wesleaConfig = createWesleaConfigFromDraw(draw);
    wesleaConfigs[wesleaConfig.drawId] = wesleaConfig;
    draw.wesleaConfig = wesleaConfig;
  }
  // console.log('Returning');
  return {draws, wesleaConfigs};
};


/**
 * Test data to check against pavexpress LEA module
 */
export const testWesleaSimulation = (): IWesleaConfig => {
  const config: IWesleaConfig = {
    drawId: "0",
    layers: [
      {modulus: 500000, poissons: 0.35, thickness: 6},
      {modulus: 28000, poissons: 0.4, thickness: 10},
    ],
    subgrademodulus: 10000,
    subgradepoissons: 0.6,
    layerinterface: [1, 1, 1],
    loads: [
      {pressure: 100, radius: 4.37, x: 0, y: 0},
      {pressure: 100, radius: 4.37, x: 82, y: 0},
    ],
    criticallocations: [
      {i: 0, l: 1, x: 0, y: 0, z: 4},
      {i: 0, l: 1, x: 82, y: 0, z: 4},
      {i: 1, l: 1, x: 0, y: 0, z: 10},
      {i: 1, l: 1, x: 82, y: 0, z: 10},
      {i: 2, l: 3, x: 0, y: 0, z: 10},
      {i: 2, l: 3, x: 82, y: 0, z: 10},
    ],
  };

  return config;
};
